import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router'
import { api } from '../api/client'
import type { Sonderfall, Versorgungspunkt } from '../api/typen'
import AppRahmen from '../komponenten/AppRahmen'
import SonderfallSchritte from '../komponenten/SonderfallSchritte'

/**
 * Schritt 7, erster Teil: einen Sonderfall an einem Versorgungspunkt anlegen. Fuer Eckregale,
 * Holzregale und Gitterkoerbe gibt es bewusst keine Bilderkennung (P7-01): Bezeichnung, Freitext und
 * die Zahl der Ebenen, danach Gesamtfoto und Ebenen der Reihe nach (P7-02).
 */
export default function SonderfallNeu() {
  const { vpId } = useParams()
  const navigiere = useNavigate()
  const abfragen = useQueryClient()

  const [bezeichnung, setBezeichnung] = useState('')
  const [beschreibung, setBeschreibung] = useState('')
  const [anzahlEbenen, setAnzahlEbenen] = useState(3)

  const punkt = useQuery({
    queryKey: ['versorgungspunkt', vpId],
    queryFn: () => api.get<Versorgungspunkt>(`/api/versorgungspunkte/${vpId}`),
  })

  const anlegen = useMutation({
    mutationFn: () =>
      api.post<Sonderfall>(`/api/versorgungspunkte/${vpId}/sonderfaelle`, {
        bezeichnung,
        beschreibung,
        anzahlEbenen,
      }),
    onSuccess: (neuer) => {
      abfragen.setQueryData(['sonderfall', String(neuer.id)], neuer)
      abfragen.invalidateQueries({ queryKey: ['versorgungspunkt', vpId] })
      navigiere(`/sonderfaelle/${neuer.id}/gesamtfoto?ablauf=1`, { replace: true })
    },
  })

  return (
    <AppRahmen
      titel="Sonderfall erfassen"
      unter="Schritt 1 von 3 · Angaben"
      zurueck={`/versorgungspunkte/${vpId}`}
      pfad={punkt.data?.pfad}
      ebene="sonderfall"
      pfadZusatz="Neuer Sonderfall"
      fuss={
        <button
          className="btn btn-primaer"
          disabled={!bezeichnung.trim() || anlegen.isPending}
          onClick={() => anlegen.mutate()}
        >
          Weiter zum Gesamtfoto
        </button>
      }
    >
      <SonderfallSchritte aktuell="angaben" />

      <p className="sek">
        Für Eckregale, Holzregale und Gitterkörbe gibt es keine automatische Bewertung. Erst kommt ein
        Gesamtfoto der ganzen Situation, danach werden die Ebenen von oben nach unten einzeln fotografiert und
        zugeordnet.
      </p>

      <div className="feld">
        <label className="label" htmlFor="bezeichnung">
          Bezeichnung
        </label>
        <input
          id="bezeichnung"
          type="text"
          placeholder="z. B. Übereckregal links, Holzregal Kellerlager"
          value={bezeichnung}
          onChange={(ereignis) => setBezeichnung(ereignis.target.value)}
        />
      </div>

      <div className="feld">
        <label className="label" htmlFor="beschreibung">
          Beschreibung
        </label>
        <textarea
          id="beschreibung"
          placeholder="Bauart, Besonderheiten, Maße soweit bekannt"
          value={beschreibung}
          onChange={(ereignis) => setBeschreibung(ereignis.target.value)}
        />
      </div>

      <div className="gruppe">
        <span className="label">Anzahl Ebenen</span>
        <span className="sek">Regalböden bzw. Ebenen, gezählt von oben nach unten.</span>
        <div className="stepper">
          <button onClick={() => setAnzahlEbenen((wert) => Math.max(1, wert - 1))} aria-label="weniger">
            −
          </button>
          <span className="wert">{anzahlEbenen}</span>
          <button onClick={() => setAnzahlEbenen((wert) => Math.min(20, wert + 1))} aria-label="mehr">
            +
          </button>
        </div>
      </div>

      {anlegen.isError && <p className="hinweiszeile">{(anlegen.error as Error).message}</p>}
    </AppRahmen>
  )
}
